import { URLSearchParams } from 'url';
import { create } from 'zustand';

export type TemplateOrderBy = 'purpose-asc' | 'theme-asc';
export type TemplatePlans = 'STARTER' | 'PRO' | 'ENTERPRISE';

export type GraphicOrderBy =
  | 'name-asc'
  | 'name-desc'
  | 'createdat-asc'
  | 'createdat-desc'
  | 'updatedat-asc'
  | 'updatedat-desc';

export type TemplateSelectedFilter = {
  purposeKeys: string[];
  resolutionIds: string[];
  themeKeys: string[];
  orderBy: TemplateOrderBy;
  plans: TemplatePlans;
};

export type GraphicSelectedFilter = {
  name: string | undefined;
  orderBy: GraphicOrderBy;
};

interface SearchParams {
  pathname: string;
  query: URLSearchParams;
}

interface SearchState {
  templateFilters: TemplateSelectedFilter;
  graphicFilters: GraphicSelectedFilter;
  searchParams: SearchParams | null;
}

interface SearchActions {
  setPurposeFilter: (purposeKeys: string[]) => void;
  setResolutionFilter: (resolutionIds: string[]) => void;
  setThemeFilter: (themeKeys: string[]) => void;
  setTemplateOrderBy: (orderBy: TemplateSelectedFilter['orderBy']) => void;
  setTemplatePlans: (plans: TemplateSelectedFilter['plans']) => void;

  setGraphicNameFilter: (name: string) => void;
  setGraphicOrderBy: (orderBy: GraphicSelectedFilter['orderBy']) => void;

  clearAllTemplateFilters: () => void;
  clearAllGraphicFilters: () => void;

  setSearchParams: (searchParams: SearchParams) => void;
  getSearchParams: () => SearchParams | null;
  clearSearchParams: () => void;

  reset: () => void;
}

const initialState: SearchState = {
  templateFilters: {
    purposeKeys: [],
    resolutionIds: [],
    themeKeys: [],
    orderBy: 'purpose-asc',
    plans: 'STARTER'
  },
  graphicFilters: {
    name: undefined, // undefined means no filter, needed for welcome page
    orderBy: 'updatedat-desc'
  },
  searchParams: null
};

export const useSearchStore = create<SearchState & SearchActions>(
  (set, get) => ({
    ...initialState,

    /* template filters */
    /* purpose filters */
    setPurposeFilter: (purposeKeys) => {
      const { templateFilters } = get();
      set({
        templateFilters: {
          ...templateFilters,
          purposeKeys: [...purposeKeys]
        }
      });
    },

    /* resolution filters */
    setResolutionFilter: (resolutionIds) => {
      const { templateFilters } = get();
      set({
        templateFilters: {
          ...templateFilters,
          resolutionIds: [...resolutionIds]
        }
      });
    },

    /* theme filters */
    setThemeFilter: (themeKeys) => {
      const { templateFilters } = get();
      set({
        templateFilters: {
          ...templateFilters,
          themeKeys: [...themeKeys]
        }
      });
    },

    /* order by */
    setTemplateOrderBy: (orderBy) => {
      const { templateFilters } = get();
      set({
        templateFilters: {
          ...templateFilters,
          orderBy
        }
      });
    },

    setTemplatePlans: (plans) => {
      const { templateFilters } = get();
      set({
        templateFilters: {
          ...templateFilters,
          plans
        }
      });
    },

    /* graphic filters */

    /* search by name */
    setGraphicNameFilter: (name) => {
      const { graphicFilters } = get();
      set({
        graphicFilters: {
          ...graphicFilters,
          name
        }
      });
    },

    /* order by */
    setGraphicOrderBy: (orderBy) => {
      const { graphicFilters } = get();
      set({
        graphicFilters: {
          ...graphicFilters,
          orderBy
        }
      });
    },

    /* reset all filters */
    clearAllTemplateFilters: () => {
      set({
        templateFilters: {
          ...initialState.templateFilters,
          orderBy: get().templateFilters.orderBy
        }
      });
    },
    clearAllGraphicFilters: () => {
      set({
        graphicFilters: initialState.graphicFilters
      });
    },

    /* search strings */
    setSearchParams: (searchParams) => {
      set({
        searchParams: searchParams
      });
    },
    getSearchParams: () => {
      if (get().searchParams) {
        return get().searchParams;
      }
      return null;
    },
    clearSearchParams: () => {
      set({
        searchParams: null
      });
    },

    reset: () => {
      set(initialState);
    }
  })
);
